import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import _ from 'lodash/';
import classNames from 'classnames';

import Type, { Heading } from '@nerdwallet/react-typography';
import Image from '@nerdwallet/react-image';
import Disclosure, { disclosureTypes } from '../disclosure/disclosure';
import parseAwardsFormatting from './lib/parse-awards-formatting';

import {
  boolCheckboxSpec,
  textColorPicker,
  subtitleInput,
  stringSelectSpec,
  stringInputSpec,
  stringTextAreaSpec,
} from '../../lib/editing-configs';

import BackgroundColor from '../background-color/background-color';
import './awards-hero.less';

const AwardsHero = ({
  backgroundIcon,
  backgroundIconMobile,
  animatedIcon,
  backgroundSize,
  backgroundImageSrcDesktop,
  backgroundImageSrcTablet,
  backgroundImageSrcMobile,
  bgOptions,
  heroType2021,
  awardsHeroType,
  preheader,
  textColor,
  subtitle,
  subtitleMobile,
  subtitleSize,
  disclosureType,
  disclosureOnBackground,
  title,
  size,
  selectTopLeftContent,
}) => {
  const heroImageSrcMap = {
    lg: backgroundImageSrcDesktop,
    md: backgroundImageSrcTablet,
    sm: backgroundImageSrcMobile,
  };

  const backgroundIconSrcMap = {
    lg: backgroundIcon,
    md: backgroundIconMobile,
    sm: backgroundIconMobile,
  };

  const awardsBadgeLogo =
    'https://www.nerdwallet.com/cdn/NW-awards/2020/hero/NW-awards-badge.png';

  const awardsTextColor = 'green';

  // align items using flexbox align-center to vertically align hero content if no advertiserDisclosure or awardsLogo is present
  const mobileFlexboxRule =
    disclosureType !== 'none' || selectTopLeftContent === 'awardsLogo'
      ? 'flex-start'
      : 'center';

  const heroContentFlexboxRule = {
    lg: 'center',
    md: 'center',
    sm: mobileFlexboxRule,
  };

  // 2020: if advertiserDisclosure or awardsLogo is present, apply top margin to hero title to vertically align hero content
  // 2021: apply bespoke title margins for each type of landing page, per viewport size
  const typeOfMargin =
    selectTopLeftContent === 'awardsLogo' ||
    (heroType2021 && awardsHeroType === '2021LP2')
      ? '67px'
      : '30px';

  const mobileTypeOfMargin =
    selectTopLeftContent === 'awardsLogo' ||
    (heroType2021 && awardsHeroType !== '2021LP2')
      ? '67px'
      : '30px';

  const mobileTitleMarginRule =
    disclosureType !== 'none' || selectTopLeftContent === 'awardsLogo'
      ? typeOfMargin
      : '0';

  // 2020: if advertiserDisclosure or awardsLogo is present, apply bottom margin to subtitle to vertically align hero content
  // 2021: apply bespoke subtitle margins for each type of landing page, per viewport size
  const heroTitleTopMarginRule = {
    lg: '0',
    md: '0',
    sm: mobileTitleMarginRule,
  };

  const mobileSubtitleMarginRule =
    disclosureType !== 'none' || selectTopLeftContent === 'awardsLogo'
      ? mobileTypeOfMargin
      : '0';

  const heroSubtitleBottomMarginRule = {
    lg: '0',
    md: '0',
    sm: mobileSubtitleMarginRule,
  };

  // show non-NDS colors and bespoke margins per hero year and type
  const subtitleTextClass = heroType2021
    ? 'awards-2021-hero__subtitle'
    : 'standard-hero__subtitle';

  // determine css layout class per awards year, and if 2021 per page level
  const layout2021 = awardsHeroType === '2021LP1' ? 'lp1-2021' : 'lp2-2021';
  const layout = heroType2021 ? layout2021 : 'lp-2020';

  return (
    <div
      className={classNames(['awards-hero__background-wrapper', `${layout}`])}
    >
      <BackgroundColor bgOptions={bgOptions}>
        {(backgroundColor) => (
          <div
            className={classNames(['awards-hero', `${layout}`])}
            style={{
              backgroundColor,
              backgroundImage: size && `url(${heroImageSrcMap[size]})`,
              backgroundSize: `${backgroundSize}`,
              alignItems: `${heroContentFlexboxRule[size]}`,
            }}
          >
            <div className={classNames(['awards-hero__content', `${layout}`])}>
              {heroType2021 &&
                awardsHeroType === '2021LP1' &&
                backgroundIcon && (
                  <Image
                    src={`${backgroundIconSrcMap[size]}`}
                    alt="Best Of Awards 2021"
                    backgroundColor="transparent"
                    className="awards-hero__background-icon"
                  />
                )}
              {heroType2021 && awardsHeroType === '2021LP1' && animatedIcon && (
                <div
                  className="awards-hero__animated-icon"
                  style={{
                    backgroundImage: `url(${animatedIcon})`,
                  }}
                />
              )}
              {selectTopLeftContent === 'awardsLogo' && (
                <Image
                  src={awardsBadgeLogo}
                  alt="Best Of Awards 2020 badge"
                  className="awards-hero__badge-icon"
                  backgroundColor="transparent"
                />
              )}
              {selectTopLeftContent === 'advertiserDisclosure' &&
                disclosureType &&
                disclosureType !== 'none' && (
                  <div className="awards-hero__advertiser-disclosure">
                    <Disclosure
                      type={disclosureType}
                      onBackground={disclosureOnBackground}
                    />
                  </div>
                )}
              {heroType2021 && awardsHeroType === '2021LP1' && preheader && (
                <Type
                  className="awards-hero__preheader"
                  color={textColor}
                  size="3"
                  bold
                >
                  {preheader}
                </Type>
              )}
              <Heading
                className={classNames(['awards-hero__title', `${layout}`])}
                size="5"
                color={awardsTextColor}
                style={{
                  marginTop:
                    heroType2021 && awardsHeroType === '2021LP1'
                      ? '20px'
                      : `${heroTitleTopMarginRule[size]}`,
                }}
              >
                {parseAwardsFormatting(title)}
              </Heading>
              <Type
                className={classNames([subtitleTextClass, `${layout}`])}
                color={textColor}
                size={(size === 'sm' && '2') || subtitleSize}
                style={{
                  marginBottom: `${heroSubtitleBottomMarginRule[size]}`,
                }}
              >
                {(size === 'sm' && subtitleMobile) || subtitle}
              </Type>
            </div>
          </div>
        )}
      </BackgroundColor>
    </div>
  );
};

AwardsHero.propTypes = {
  heroType2021: PropTypes.bool,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  subtitleMobile: PropTypes.string,
  subtitleSize: PropTypes.oneOf(['2', '3']),
  backgroundIcon: PropTypes.string,
  backgroundIconMobile: PropTypes.string,
  animatedIcon: PropTypes.string,
  backgroundSize: PropTypes.oneOf(['contain', 'cover', 'auto']),
  backgroundImageSrcDesktop: PropTypes.string,
  backgroundImageSrcTablet: PropTypes.string,
  backgroundImageSrcMobile: PropTypes.string,
  awardsHeroType: PropTypes.oneOf(['2021LP1', '2021LP2']),
  disclosureType: PropTypes.oneOf([...disclosureTypes, 'none']),
  disclosureOnBackground: PropTypes.bool,
  preheader: PropTypes.string,
  selectTopLeftContent: PropTypes.oneOf(['advertiserDisclosure', 'awardsLogo']),
  size: PropTypes.string,
  ...BackgroundColor.propTypes,
  textColor: PropTypes.string,
};

AwardsHero.defaultProps = {
  heroType2021: false,
  title: 'Title goes here',
  subtitle:
    'Description (desktop) goes here. Mobile will default to this if no mobile subtitle provided.',
  subtitleSize: '2',
  ...BackgroundColor.defaultProps,
  backgroundIcon: '',
  backgroundIconMobile: '',
  animatedIcon: '',
  backgroundSize: 'contain',
  backgroundImageSrcDesktop: '',
  backgroundImageSrcTablet: '',
  backgroundImageSrcMobile: '',
  awardsHeroType: '',
  preheader: '',
  selectTopLeftContent: 'advertiserDisclosure',
  size: '',
};

AwardsHero.getEditingConfig = (componentDocument) => {
  const show2021LayoutDropdown = _.get(
    componentDocument,
    'props.heroType2021',
    true
  )
    ? [
        stringSelectSpec('awardsHeroType', '2021 Awards Hero Type', [
          { label: '2021 LP1', value: '2021LP1' },
          { label: '2021 LP2', value: '2021LP2' },
        ]),
      ]
    : [''];

  const show2021LP1EditingConfig =
    _.get(componentDocument, 'props.heroType2021', true) &&
    _.get(componentDocument, 'props.awardsHeroType') === '2021LP1'
      ? [
          stringInputSpec('backgroundIcon', 'Background icon'),
          stringInputSpec(
            'backgroundIconMobile',
            'Background icon for mobile devices'
          ),
          stringInputSpec('animatedIcon', 'Animated Icon'),
          stringTextAreaSpec('preheader', 'Pre-header text'),
        ]
      : [''];

  const topLeftContent =
    componentDocument?.props?.selectTopLeftContent === 'advertiserDisclosure'
      ? [...Disclosure.getEditingConfig()]
      : [''];

  return [
    boolCheckboxSpec('heroType2021', '2021 Awards hero?'),
    ...show2021LayoutDropdown,
    ...BackgroundColor.getEditingConfig(componentDocument),
    ...show2021LP1EditingConfig,
    stringTextAreaSpec('title', 'Title', {
      help:
        'For yellow underline styling, surround title text with | like |this|. For outline font styling, surround title text with * like *this*. For 2021 styling, surround title with ^ like ^this^. For 2021 italicized styling, surround title text with a & like $this$. To include a linebreak, insert :br: in the textbox.',
    }),
    subtitleInput,
    stringTextAreaSpec('props.subtitleMobile', 'Mobile subtitle'),
    stringSelectSpec(
      'props.subtitleSize',
      'Subtitle Text Size',
      [
        { label: 'Medium (default)', value: '2' },
        { label: 'Large', value: '3' },
      ],
      {
        help: 'Select the size of the subtitle text',
      }
    ),
    textColorPicker,
    stringSelectSpec(
      'backgroundSize',
      'Background Size',
      [
        { label: 'Contain', value: 'contain' },
        { label: 'Cover', value: 'cover' },
        { label: 'Auto', value: 'auto' },
      ],
      {
        help: 'Only applies to Desktop and Tablet',
      }
    ),
    stringInputSpec(
      'backgroundImageSrcDesktop',
      'Background Image URL (Desktop)'
    ),
    stringInputSpec(
      'backgroundImageSrcTablet',
      'Background Image URL (Tablet)'
    ),
    stringInputSpec(
      'backgroundImageSrcMobile',
      'Background Image URL (Mobile)'
    ),
    stringSelectSpec(
      'selectTopLeftContent',
      'Content displayed in top left corner of hero',
      [
        { label: 'Advertiser disclosure', value: 'advertiserDisclosure' },
        { label: 'NW Awards logo', value: 'awardsLogo' },
        { label: 'Nothing', value: 'nothing' },
      ],
      {
        help:
          'To output a Best Of Awards badge icon, select "NW Awards logo" from the dropdown. To output an advertiser disclosure, select "Advertiser disclosure" from the dropdown and then the type of disclosure to be output from the "Advertiser disclosure popup" dropdown. To output nothing, select "Advertiser disclosure" from the dropdown and "None" from the "Advertiser disclosure popup" dropdown.',
      }
    ),
    ...topLeftContent,
  ];
};

AwardsHero.displayName = 'AwardsHero';

export default connect(
  createStructuredSelector({
    size: _.property('mediaQuery'),
  })
)(AwardsHero);
